/* VX asset: vx-cart.js — Velaxen × Resilia rebuild (VX25)
   Cart drawer engine: fetches /cart.js, renders line items client-side,
   AJAX add-to-cart interception, count sync, open/close + focus handling.
   Safe against double-execution (theme editor section re-render). */
(function () {
  'use strict';

  if (window.__vxCartInit) return;
  window.__vxCartInit = true;

  var routes = window.routes || {};
  var ROOT = (routes.root_url || '/').replace(/\/$/, '');
  var CART_URL = routes.cart_url || ROOT + '/cart';
  var ADD_URL = routes.cart_add_url || ROOT + '/cart/add';
  var CHANGE_URL = routes.cart_change_url || ROOT + '/cart/change';

  var state = {
    cart: null,
    open: false,
    fetching: null,
    lastFocus: null,
    priority: null,        /* { id, price } resolved upsell variant */
    priorityMissing: false,
    compareAt: {},         /* variant id -> compare_at_price (cents) */
    handlesFetched: {},    /* product handle -> true once /products/<handle>.js was requested */
    pendingQty: {},        /* line key -> optimistic target quantity */
    clampKey: null,
    queue: Promise.resolve()
  };

  var qtyTimers = {};

  var SVG = {
    minus: '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M5 12h14"/></svg>',
    plus: '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M12 5v14M5 12h14"/></svg>',
    x: '<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18"/></svg>'
  };

  /* ---------- utilities ---------- */

  function drawer() {
    return document.querySelector('[data-vx-cart]');
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function moneyText(cents) {
    var out = null;
    if (window.VX && typeof window.VX.money === 'function') {
      try { out = window.VX.money(cents); } catch (e) { out = null; }
    }
    if (out == null && window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try { out = window.Shopify.formatMoney(cents); } catch (e) { out = null; }
    }
    if (out == null) out = '$' + (Math.round(cents) / 100).toFixed(2);
    return String(out).replace(/<[^>]*>/g, '');
  }

  function thumbUrl(u) {
    if (!u) return '';
    try {
      var url = new URL(u, window.location.origin);
      url.searchParams.set('width', '144');
      return url.toString();
    } catch (e) { return u; }
  }

  /* Line prices: /cart.js does not expose compare_at_price on line items in
     all shops, so savings = original vs final line price, upgraded to
     compare_at when the payload or the /products/<handle>.js cache carries it. */
  function linePrices(item) {
    var finalLine = item.final_line_price != null ? item.final_line_price : item.line_price;
    var compareLine = item.original_line_price != null ? item.original_line_price : finalLine;
    var unitFinal = item.final_price != null ? item.final_price : item.price;
    var compareUnit = item.compare_at_price;
    if (!compareUnit) {
      var vid = item.variant_id != null ? item.variant_id : item.id;
      if (vid != null && state.compareAt[vid]) compareUnit = state.compareAt[vid];
    }
    if (compareUnit && compareUnit > unitFinal) {
      var c = compareUnit * item.quantity;
      if (c > compareLine) compareLine = c;
    }
    return { final: finalLine, compare: compareLine, hasCompare: compareLine > finalLine };
  }

  /* ---------- reservation hold (session countdown) ---------- */

  function holdKey() {
    var d = drawer();
    var el = d && d.querySelector('.vx-cart__hold vx-countdown');
    return (el && el.getAttribute('data-key')) || 'vx-cart-hold-session';
  }

  function clearHoldKey() {
    try { sessionStorage.removeItem(holdKey()); } catch (e) {}
  }

  function startHold() {
    if (window.VX && typeof window.VX.startHold === 'function') {
      try { window.VX.startHold(); } catch (e) {}
    }
  }

  /* ---------- cart API ---------- */

  function fetchCartOnce() {
    return fetch(CART_URL + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('cart fetch blocked');
        return r.text();
      })
      .then(function (t) {
        try { return JSON.parse(t); } catch (e) { throw new Error('cart fetch non-json'); }
      });
  }

  function fetchCart() {
    if (state.fetching) return state.fetching;
    state.fetching = fetchCartOnce()
      .catch(function () {
        return new Promise(function (res) { setTimeout(res, 900); }).then(fetchCartOnce);
      })
      .then(function (cart) {
        state.cart = cart;
        primeCompareAt(cart);
        render();
        return cart;
      })
      .catch(function () { return state.cart; })
      .finally(function () { state.fetching = null; });
    return state.fetching;
  }

  /* Fetch /products/<handle>.js once per unique handle to recover
     compare_at_price for line items whose cart payload lacks it. */
  function primeCompareAt(cart) {
    if (!cart || !cart.items) return;
    cart.items.forEach(function (item) {
      var handle = item.handle;
      if (!handle || state.handlesFetched[handle]) return;
      state.handlesFetched[handle] = true;
      fetch(ROOT + '/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) {
          if (!r.ok) throw new Error('missing');
          return r.json();
        })
        .then(function (p) {
          var hit = false;
          (p.variants || []).forEach(function (v) {
            if (v && v.id != null && v.compare_at_price) {
              state.compareAt[v.id] = v.compare_at_price;
              hit = true;
            }
          });
          if (hit && state.cart) render();
        })
        .catch(function () {});
    });
  }

  function addItems(items) {
    return fetch(ADD_URL + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (r) {
      return r.json().then(function (data) {
        if (!r.ok) throw data;
        return data;
      });
    });
  }

  function changeLine(key, qty) {
    var d = drawer();
    if (d) {
      var row = d.querySelector('.vx-cart__item[data-key="' + key + '"]');
      if (row) row.classList.add('is-busy');
    }
    var run = function () {
      return fetch(CHANGE_URL + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty })
      })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          if (cart && cart.items) {
            state.cart = cart;
            primeCompareAt(cart);
            render();
          } else {
            return fetchCart();
          }
        })
        .catch(function () { return fetchCart(); });
    };
    state.queue = state.queue.then(run, run);
    return state.queue;
  }

  /* Debounced optimistic quantity stepper: repeated taps merge into one
     /cart/change.js call with the final target quantity. */
  function queueQtyChange(key, target, row) {
    state.pendingQty[key] = target;
    var num = row && row.querySelector('.vx-cart__qty-num');
    if (num) num.textContent = target;
    clearTimeout(qtyTimers[key]);
    qtyTimers[key] = setTimeout(function () {
      delete qtyTimers[key];
      var qty = state.pendingQty[key];
      delete state.pendingQty[key];
      if (qty == null) return;
      var current = null;
      if (state.cart) {
        for (var i = 0; i < state.cart.items.length; i++) {
          if (state.cart.items[i].key === key) { current = state.cart.items[i].quantity; break; }
        }
      }
      if (current === qty) { render(); return; }
      changeLine(key, qty);
    }, 250);
  }

  /* ---------- priority processing upsell ---------- */

  function resolvePriority() {
    var d = drawer();
    var card = d && d.querySelector('[data-vx-priority]');
    if (!card) return Promise.resolve(null);
    if (state.priority) return Promise.resolve(state.priority);
    if (state.priorityMissing) return Promise.resolve(null);

    var vid = parseInt(card.getAttribute('data-variant-id'), 10);
    if (vid) {
      var price = parseInt(card.getAttribute('data-variant-price'), 10);
      state.priority = { id: vid, price: isNaN(price) ? null : price };
      return Promise.resolve(state.priority);
    }

    var handle = card.getAttribute('data-handle') || 'priority-processing';
    return fetch(ROOT + '/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('missing');
        return r.json();
      })
      .then(function (p) {
        var v = null;
        for (var i = 0; i < (p.variants || []).length; i++) {
          if (p.variants[i].available) { v = p.variants[i]; break; }
        }
        v = v || (p.variants && p.variants[0]);
        if (!v) throw new Error('missing');
        state.priority = { id: v.id, price: v.price };
        return state.priority;
      })
      .catch(function () {
        state.priorityMissing = true;
        return null;
      });
  }

  /* Priority Processing is a fee, not a product: match a line item against
     the resolved variant id first, section-picked/fallback handle second. */
  function isPriorityItem(item) {
    if (!item) return false;
    var d = drawer();
    var row = d && d.querySelector('[data-vx-priority]');
    if (!row) return false;
    var pid = state.priority && state.priority.id;
    var handle = row.getAttribute('data-handle');
    if (pid && item.id === pid) return true;
    if (handle && item.handle === handle) return true;
    return false;
  }

  function findPriorityItem(cart) {
    if (!cart) return null;
    for (var i = 0; i < cart.items.length; i++) {
      if (isPriorityItem(cart.items[i])) return cart.items[i];
    }
    return null;
  }

  /* Fee invariants: quantity never exceeds 1, and the fee never sits in the
     cart alone (a processing fee with nothing to process is removed). */
  function clampPriority(cart) {
    var item = findPriorityItem(cart);
    if (!item) return;
    if (state.clampKey === item.key) return;
    var others = false;
    for (var i = 0; i < cart.items.length; i++) {
      if (!isPriorityItem(cart.items[i])) { others = true; break; }
    }
    var target = null;
    if (!others) target = 0;
    else if (item.quantity > 1) target = 1;
    if (target == null) return;
    state.clampKey = item.key;
    changeLine(item.key, target).finally(function () { state.clampKey = null; });
  }

  function priorityToggle(btn) {
    if (btn.disabled) return;
    var on = btn.getAttribute('aria-checked') === 'true';
    btn.disabled = true;
    btn.classList.add('is-loading', 'is-pending');
    btn.setAttribute('aria-checked', on ? 'false' : 'true');
    var prow = btn.closest('[data-vx-priority]');
    if (prow) prow.classList.toggle('is-on', !on);
    var op;
    if (on) {
      var item = findPriorityItem(state.cart);
      op = item
        ? fetch(CHANGE_URL + '.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: item.key, quantity: 0 })
          }).then(function (r) { return r.json(); })
        : Promise.resolve(null);
    } else {
      op = resolvePriority().then(function (p) {
        if (!p) return null;
        return addItems([{ id: p.id, quantity: 1 }]);
      });
    }
    op
      .then(function () { return fetchCart(); })
      .catch(function () { /* leave cart as-is */ })
      .finally(function () {
        btn.classList.remove('is-loading', 'is-pending');
        btn.disabled = false;
        render();
      });
  }

  /* ---------- rendering ---------- */

  function syncCounts() {
    if (!state.cart) return;
    var n = state.cart.item_count;
    var p = findPriorityItem(state.cart);
    if (p) n = Math.max(0, n - p.quantity);
    document.querySelectorAll('[data-vx-cart-count]').forEach(function (el) {
      el.textContent = n;
      el.classList.toggle('is-zero', n === 0);
    });
  }

  function itemHtml(item) {
    var p = linePrices(item);
    var note = '';
    if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
      note = item.selling_plan_allocation.selling_plan.name;
    } else if (item.variant_title && item.variant_title !== 'Default Title') {
      note = item.variant_title;
    } else {
      note = 'One-time purchase';
    }
    var qty = state.pendingQty[item.key] != null ? state.pendingQty[item.key] : item.quantity;
    var img = thumbUrl(item.image);
    return (
      '<div class="vx-cart__item" data-key="' + esc(item.key) + '">' +
        '<div class="vx-cart__thumb">' +
          (img
            ? '<img src="' + esc(img) + '" alt="' + esc(item.product_title) + '" width="72" height="72" loading="lazy">'
            : '') +
        '</div>' +
        '<div class="vx-cart__meta">' +
          '<p class="vx-cart__item-title">' + esc(item.product_title) + '</p>' +
          '<p class="vx-cart__item-note">' + esc(note) + '</p>' +
          '<div class="vx-cart__qty">' +
            '<button type="button" class="vx-cart__qty-btn" data-vx-qty="minus" aria-label="Decrease quantity of ' + esc(item.product_title) + '">' + SVG.minus + '</button>' +
            '<span class="vx-cart__qty-num">' + qty + '</span>' +
            '<button type="button" class="vx-cart__qty-btn" data-vx-qty="plus" aria-label="Increase quantity of ' + esc(item.product_title) + '">' + SVG.plus + '</button>' +
          '</div>' +
        '</div>' +
        '<div class="vx-cart__item-right">' +
          '<span class="vx-cart__item-price">' +
            (p.hasCompare ? '<s>' + esc(moneyText(p.compare)) + '</s> ' : '') +
            '<span class="vx-cart__item-price-final">' + esc(moneyText(p.final)) + '</span>' +
          '</span>' +
          '<button type="button" class="vx-cart__remove" data-vx-remove aria-label="Remove ' + esc(item.product_title) + '">' + SVG.x + '</button>' +
        '</div>' +
      '</div>'
    );
  }

  function renderItems(d, cart) {
    var wrap = d.querySelector('[data-vx-cart-items]');
    if (!wrap) return;

    /* keep keyboard focus on the same stepper across re-renders */
    var fKey = null, fAction = null;
    var focused = document.activeElement;
    if (focused && wrap.contains(focused) && focused.hasAttribute && focused.hasAttribute('data-vx-qty')) {
      fAction = focused.getAttribute('data-vx-qty');
      var row = focused.closest('[data-key]');
      fKey = row && row.getAttribute('data-key');
    }

    /* the Priority Processing fee is controlled by the footer toggle only —
       no thumbnail/stepper/remove row for it */
    var items = [];
    for (var i = 0; i < cart.items.length; i++) {
      if (!isPriorityItem(cart.items[i])) items.push(cart.items[i]);
    }
    wrap.innerHTML = items.map(itemHtml).join('');

    if (fKey) {
      var again = wrap.querySelector('[data-key="' + fKey + '"] [data-vx-qty="' + fAction + '"]');
      if (again) again.focus();
    }
  }

  function renderShipping(d, cart) {
    var el = d.querySelector('[data-vx-shipping]');
    if (!el) return;
    var threshold = parseInt(el.getAttribute('data-threshold'), 10) || 0;
    if (threshold <= 0) { el.hidden = true; return; }
    var total = cart.total_price || 0;
    var p = findPriorityItem(cart);
    if (p) {
      var fee = p.final_line_price != null ? p.final_line_price : p.line_price;
      total = Math.max(0, total - (fee || 0));
    }
    var remaining = threshold - total;
    var pct = Math.max(0, Math.min(100, Math.round((total / threshold) * 100)));
    var fill = el.querySelector('[data-vx-shipping-fill]');
    if (fill) {
      fill.style.display = 'block';
      fill.style.width = pct + '%';
      fill.style.minWidth = pct > 0 ? '8px' : '';
    }
    var msg = el.querySelector('[data-vx-shipping-msg]');
    if (msg) {
      if (remaining > 0) {
        msg.innerHTML = esc(el.getAttribute('data-msg-progress') || '')
          .replace('[amount]', '<b>' + esc(moneyText(remaining)) + '</b>');
      } else {
        msg.innerHTML = esc(el.getAttribute('data-msg-unlocked') || '');
      }
    }
    el.classList.toggle('is-unlocked', remaining <= 0);
  }

  function renderTotals(d, cart) {
    var sub = d.querySelector('[data-vx-cart-subtotal]');
    if (sub) sub.textContent = moneyText(cart.total_price);
    var savings = 0;
    for (var i = 0; i < cart.items.length; i++) {
      if (isPriorityItem(cart.items[i])) continue;
      var p = linePrices(cart.items[i]);
      if (p.hasCompare) savings += p.compare - p.final;
    }
    var sav = d.querySelector('[data-vx-cart-savings]');
    if (sav) {
      if (savings > 0) {
        sav.hidden = false;
        sav.innerHTML = esc(sav.getAttribute('data-template') || '')
          .replace('[amount]', '<b>' + esc(moneyText(savings)) + '</b>');
      } else {
        sav.hidden = true;
        sav.innerHTML = '';
      }
    }
  }

  function renderPriority(d, cart) {
    var row = d.querySelector('[data-vx-priority]');
    var card = d.querySelector('[data-vx-upsell-card]');
    if (!row) {
      if (card) card.hidden = true;
      return;
    }
    if (state.priorityMissing) {
      row.hidden = true;
      if (card) card.hidden = true;
      return;
    }
    row.hidden = false;

    var inCart = !!findPriorityItem(cart);
    row.classList.toggle('is-on', inCart);
    var sw = row.querySelector('[data-vx-priority-toggle]');
    if (sw && !sw.disabled) sw.setAttribute('aria-checked', inCart ? 'true' : 'false');
    if (card) card.hidden = inCart;
    if (state.priority && state.priority.price != null) {
      var priceEl = row.querySelector('[data-vx-priority-price]');
      if (priceEl) priceEl.textContent = '+' + moneyText(state.priority.price);
    }
  }

  function render() {
    syncCounts();
    var d = drawer();
    if (!d || !state.cart) return;
    var cart = state.cart;
    if (cart.item_count === 0) clearHoldKey();
    else if (state.open) startHold();
    d.classList.toggle('is-empty', cart.item_count === 0);
    renderItems(d, cart);
    renderShipping(d, cart);
    renderTotals(d, cart);
    renderPriority(d, cart);
    clampPriority(cart);
    if (typeof window.vxShrinkFootnotes === 'function') window.vxShrinkFootnotes(d);
  }

  /* ---------- open / close ---------- */

  function openDrawer(skipFetch) {
    var d = drawer();
    if (!d) return;
    if (!state.open) state.lastFocus = document.activeElement;
    state.open = true;
    d.classList.add('is-open');
    d.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    var closeBtn = d.querySelector('[data-vx-cart-close]');
    if (closeBtn) closeBtn.focus();
    if (state.cart && state.cart.item_count > 0) startHold();
    if (skipFetch !== true) fetchCart();
    resolvePriority().then(function () { if (state.cart) render(); });
  }

  function closeDrawer() {
    var d = drawer();
    state.open = false;
    if (d) {
      d.classList.remove('is-open');
      d.setAttribute('aria-hidden', 'true');
    }
    document.body.style.overflow = '';
    if (state.lastFocus && typeof state.lastFocus.focus === 'function') {
      state.lastFocus.focus();
    }
    state.lastFocus = null;
  }

  function trapFocus(e) {
    var d = drawer();
    var panel = d && d.querySelector('.vx-cart__panel');
    if (!panel) return;
    var nodes = panel.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])');
    var f = [];
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].offsetParent !== null || nodes[i] === document.activeElement) f.push(nodes[i]);
    }
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && (document.activeElement === first || !panel.contains(document.activeElement))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (document.activeElement === last || !panel.contains(document.activeElement))) {
      e.preventDefault();
      first.focus();
    }
  }

  /* ---------- product form interception ---------- */

  function showFormError(form, msg) {
    var el = form.querySelector('[data-vx-form-error]');
    if (!el) {
      el = document.createElement('p');
      el.className = 'vx-cart-form-error';
      el.setAttribute('data-vx-form-error', '');
      el.setAttribute('role', 'alert');
      form.appendChild(el);
    }
    el.textContent = msg || 'Something went wrong. Please try again.';
    clearTimeout(el.__vxT);
    el.__vxT = setTimeout(function () { el.textContent = ''; }, 5000);
  }

  function handleProductForm(form) {
    if (form.getAttribute('data-vx-adding')) return;
    form.setAttribute('data-vx-adding', '1');
    var btn = form.querySelector('[type="submit"]');
    if (btn) {
      btn.__vxHandled = true;
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');
      btn.disabled = true;
    }
    var fd = new FormData(form);
    fetch(ADD_URL + '.js', { method: 'POST', body: fd, headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json().then(function (data) {
          if (!r.ok) throw data;
          return data;
        });
      })
      .then(function () {
        /* The success checkmark is NOT played from here. Bundle apps (Kaching)
           intercept the click and navigate to /checkout, so this path frequently
           never runs on the live store and the animation never played. It is now
           driven from the click itself - see vxBindAtcAnimation below. */
        /* open immediately on successful add; refresh runs in parallel */
        openDrawer(true);
        return fetchCart().catch(function () {});
      })
      .catch(function (err) {
        showFormError(form, err && (err.description || err.message));
      })
      .finally(function () {
        form.removeAttribute('data-vx-adding');
        if (btn) {
          btn.classList.remove('is-loading');
          btn.removeAttribute('aria-busy');
          btn.disabled = false;
        }
      });
  }

  /* ---------- add-to-cart success animation (Horizon port) ----------
     Horizon (snippets/add-to-cart-button.liquid + assets/product-form.js) animates
     on the button's CLICK, never on a fetch result: handleClick() calls
     animateAddToCart() straight away, which sets data-added="true", yields to the
     main thread, and removes the attribute on an 800ms timeout. Because it never
     waits for a network response, a bundle app that intercepts the click and
     redirects to /checkout cannot suppress it.

     Bound in the CAPTURE phase on document so it runs before any handler attached
     to the button itself - an interceptor calling stopPropagation() on its own
     bubble-phase listener cannot stop it either.

     Applies to any .vx-btn carrying the .vx-atc-added overlay, so both buy boxes
     and the sticky bar get it without either section binding anything. */
  function vxAnimateAdded(btn) {
    /* Per-button timeout list: a rapid second click clears the pending reset from
       the first, so the state can never get stuck on. */
    if (!btn.__vxAtcTimers) btn.__vxAtcTimers = [];
    for (var i = 0; i < btn.__vxAtcTimers.length; i++) clearTimeout(btn.__vxAtcTimers[i]);
    btn.__vxAtcTimers.length = 0;
    /* Reset per click. handleProductForm sets it if OUR ajax path takes the add;
       when a bundle app owns the click it stays false and nobody else is driving
       the button's feedback. */
    btn.__vxHandled = false;
    btn.classList.remove('is-loading');

    if (btn.getAttribute('data-added') !== 'true') btn.setAttribute('data-added', 'true');

    /* Yield to the main thread first (Horizon does the same) so setting the
       attribute is not batched into the same frame as the style recalculation
       that starts the transitions. setTimeout, not requestAnimationFrame:
       rAF is PAUSED while the document is hidden, which would strand the button
       in its success state if the tab were backgrounded mid-animation. */
    var yieldId = setTimeout(function () {
      var ix0 = btn.__vxAtcTimers.indexOf(yieldId);
      if (ix0 > -1) btn.__vxAtcTimers.splice(ix0, 1);
      var id = setTimeout(function () {
        btn.removeAttribute('data-added');
        var ix = btn.__vxAtcTimers.indexOf(id);
        if (ix > -1) btn.__vxAtcTimers.splice(ix, 1);
        /* Hand the checkmark straight over to the spinner. On a bundle add the app
           is still working (often navigating to checkout) long after the tick has
           finished, and a button that goes completely inert reads as a dead click.
           Skipped when our own ajax path owns the add - that already shows the
           spinner and opens the drawer. Capped so it can never stay stuck on. */
        if (!btn.__vxHandled) {
          btn.classList.add('is-loading');
          var spin = setTimeout(function () {
            btn.classList.remove('is-loading');
            var ixs = btn.__vxAtcTimers.indexOf(spin);
            if (ixs > -1) btn.__vxAtcTimers.splice(ixs, 1);
          }, 8000);
          btn.__vxAtcTimers.push(spin);
        }
      }, 800);
      btn.__vxAtcTimers.push(id);
    }, 0);
    btn.__vxAtcTimers.push(yieldId);
  }

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var btn = t.closest('.vx-btn');
    if (!btn || btn.disabled || !btn.querySelector('.vx-atc-added')) return;
    /* Same guard as Horizon: never animate a submit the browser will reject. */
    var form = btn.closest('form');
    if (form && typeof form.checkValidity === 'function' && !form.checkValidity()) return;
    vxAnimateAdded(btn);
  }, true);

  /* ---------- delegated events (bound once, editor-safe) ---------- */

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;

    var toggle = t.closest('[data-vx-cart-toggle]');
    if (toggle) {
      e.preventDefault();
      openDrawer();
      return;
    }

    var d = drawer();
    if (!d || !d.contains(t)) return;

    if (t.closest('[data-vx-cart-close]') || t.closest('[data-vx-cart-overlay]')) {
      closeDrawer();
      return;
    }

    var pbtn = t.closest('[data-vx-priority-toggle]');
    if (pbtn) {
      priorityToggle(pbtn);
      return;
    }

    /* the whole priority row is clickable; events on the switch itself were
       already handled above so they never double-fire */
    var prow = t.closest('[data-vx-priority]');
    if (prow) {
      var psw = prow.querySelector('[data-vx-priority-toggle]');
      if (psw) priorityToggle(psw);
      return;
    }

    var ucard = t.closest('[data-vx-upsell-card]');
    if (ucard) {
      var usw = d.querySelector('[data-vx-priority-toggle]');
      if (usw) priorityToggle(usw);
      return;
    }

    var row = t.closest('.vx-cart__item[data-key]');
    if (!row) return;
    var key = row.getAttribute('data-key');

    if (t.closest('[data-vx-remove]')) {
      changeLine(key, 0);
      return;
    }
    var qbtn = t.closest('[data-vx-qty]');
    if (qbtn && state.cart) {
      var item = null;
      for (var i = 0; i < state.cart.items.length; i++) {
        if (state.cart.items[i].key === key) { item = state.cart.items[i]; break; }
      }
      if (!item) return;
      var dir = qbtn.getAttribute('data-vx-qty') === 'plus' ? 1 : -1;
      var base = state.pendingQty[key] != null ? state.pendingQty[key] : item.quantity;
      queueQtyChange(key, Math.max(0, base + dir), row);
    }
  });

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.classList || !form.classList.contains('vx-product-form')) return;
    e.preventDefault();
    handleProductForm(form);
  });

  document.addEventListener('keydown', function (e) {
    if (!state.open) return;
    if (e.key === 'Escape') closeDrawer();
    else if (e.key === 'Tab') trapFocus(e);
  });

  document.addEventListener('vx:cart:open', function () { openDrawer(); });
  document.addEventListener('vx:cart:refresh', function () { fetchCart(); });

  /* theme editor: re-init when the drawer section re-renders */
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('[data-vx-cart]')) {
      state.priority = null;
      state.priorityMissing = false;
      if (state.open) {
        var d = drawer();
        if (d) {
          d.classList.add('is-open');
          d.setAttribute('aria-hidden', 'false');
        }
      }
      init();
    }
  });

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:select', function (e) {
      if (e.target && e.target.querySelector && e.target.querySelector('[data-vx-cart]')) openDrawer();
    });
    document.addEventListener('shopify:section:deselect', function (e) {
      if (e.target && e.target.querySelector && e.target.querySelector('[data-vx-cart]')) closeDrawer();
    });
  }

  /* ---------- init ---------- */

  function init() {
    fetchCart()
      .then(function () { return resolvePriority(); })
      .then(function () { render(); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Refresh cart state when the page is restored from the back/forward cache
     (e.g. Back from /checkout), so counts and drawer contents aren't stale. */
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) {
      document.dispatchEvent(new CustomEvent('vx:cart:refresh'));
    }
  });
})();
